import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TenantMembersService } from '../../services/tenant-members.service';
import {
  AcceptedOrganogramRole,
  TenantMember
} from '../../interfaces/tenant-members.interface';
import { AuthService } from '../../../../shared/services/auth.service';

@Component({
  selector: 'app-members',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './members.component.html',
  styleUrl: './members.component.css'
})
export class MembersComponent implements OnInit {
  @Output() backClick = new EventEmitter<void>();

  members: TenantMember[] = [];
  roles: AcceptedOrganogramRole[] = [];
  draftRoleByUserId: Record<string, string> = {};
  savingUserId: string | null = null;
  isLoading = true;
  error: string | null = null;
  info: string | null = null;

  constructor(
    private membersService: TenantMembersService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.isLoading = true;
    this.error = null;
    this.info = null;

    this.membersService.listAcceptedRoles().subscribe({
      next: rolesRes => {
        this.roles = rolesRes?.data ?? [];
        this.membersService.listMembers().subscribe({
          next: membersRes => {
            this.members = membersRes?.data ?? [];
            this.draftRoleByUserId = {};
            for (const member of this.members) {
              this.draftRoleByUserId[member.userId] = member.organogramRoleId ?? '';
            }
            this.isLoading = false;
          },
          error: err => {
            this.isLoading = false;
            this.error = err?.error?.message || 'Failed to load members.';
          }
        });
      },
      error: err => {
        this.isLoading = false;
        this.error = err?.error?.message || 'Failed to load organogram roles.';
      }
    });
  }

  onBack(): void {
    this.backClick.emit();
  }

  save(member: TenantMember): void {
    const selected = this.draftRoleByUserId[member.userId] || '';
    const organogramRoleId = selected ? selected : null;
    this.savingUserId = member.userId;
    this.error = null;
    this.info = null;

    this.membersService.assignOrganogramRole(member.userId, organogramRoleId).subscribe({
      next: res => {
        this.savingUserId = null;
        const assignment = res?.data;
        member.organogramRoleId = assignment?.organogramRoleId ?? null;
        member.organogramRoleName = assignment?.organogramRoleName ?? null;
        this.draftRoleByUserId[member.userId] = member.organogramRoleId ?? '';
        this.info = res?.message || 'Role updated.';

        const currentUserId = this.authService.getUserId();
        if (currentUserId && currentUserId.toLowerCase() === member.userId.toLowerCase()) {
          this.authService.applyOrganogramIdentity(
            assignment?.organogramRoleName ?? null,
            assignment?.permissions ?? []
          );
        }
      },
      error: err => {
        this.savingUserId = null;
        this.error = err?.error?.message || 'Failed to assign role.';
      }
    });
  }

  roleLabel(roleId: string | null | undefined): string {
    if (!roleId) {
      return 'None';
    }
    return this.roles.find(r => r.id === roleId)?.role ?? 'Unknown';
  }
}
